<template>
  <section class="modal-vc" @click="handleClick" tabindex="-1">
    <div ref="backdrop" class="modal-vc__backdrop" :style="backdropStyleObject"></div>
    <div
      ref="scrollContainer"
      class="modal-vc__scroll-container"
      @scroll="handleScrollContainerScroll"
    >
      <div ref="content" class="modal-vc__content">
        <h1 class="modal-vc__header" :class="{ ['modal-vc__header--' + color]: true }">
          <span class="modal-vc__heading">{{ heading }}</span>
          <button class="modal-vc__close" @click="handleCloseClick"></button>
        </h1>
        <slot></slot>
      </div>
    </div>
  </section>
</template>

<script>
import animateProperty from '@/utils/animateProperty'

function getMaximumViewportHeight() {
  const div = document.createElement('div')
  div.style.height = '100vh'
  div.style.position = 'fixed'
  div.style.visibility = 'hidden'
  document.body.appendChild(div)
  const height = div.offsetHeight
  div.parentElement.removeChild(div)

  return height
}

export default {
  props: {
    color: {
      type: String,
      default: 'green'
    },
    heading: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      backdropOpacity: 0,
      maxScroll: null,
      scrollTop: 0
    }
  },
  computed: {
    backdropStyleObject() {
      return {
        opacity: this.backdropOpacity
      }
    }
  },
  watch: {
    backdropOpacity(value, lastValue) {
      // If we are in the process of closing, close when we get close to 0
      if (value < lastValue && value < 0.01) {
        this.$emit('close')
      }
    }
  },
  mounted() {
    const contentHeight = this.$refs.content.offsetHeight
    const heightOf100vh = window.innerHeight

    //Apply half of the remaining space as margin-bottom
    this.$refs.content.style.marginBottom = `${(heightOf100vh - contentHeight) / 2}px`

    //Read the value from the dom so we can modify it with css
    const marginBottom = Math.ceil(
      window.getComputedStyle(this.$refs.content).marginBottom.replace('px', '')
    )

    this.maxScroll = Math.min(this.$refs.scrollContainer.scrollHeight, contentHeight + marginBottom)
    this.$store.dispatch('SET_MODAL_OPEN', true)

    this.$nextTick(() => {
      requestAnimationFrame(() => {
        animateProperty(this.$refs.scrollContainer, 'scrollTop', this.maxScroll, () => {
          this.$el.focus()
        })
      })
    })
  },
  beforeDestroy() {
    if (this.closeResolver) {
      this.closeResolver()
    }
    this.$store.dispatch('SET_MODAL_OPEN', false)
  },
  methods: {
    handleScrollContainerScroll(event) {
      if (this.$refs.scrollContainer) {
        const scrollTop = this.$refs.scrollContainer.scrollTop

        this.backdropOpacity = Math.max(Math.min(scrollTop / this.maxScroll, 1), 0)
      }
    },
    handleClick(event) {
      if (event.target === this.$refs.backdrop || event.target === this.$refs.scrollContainer) {
        this.close()
      }
    },
    handleCloseClick() {
      this.close()
    },
    close() {
      return new Promise(resolve => {
        this.closeResolver = resolve
        animateProperty(this.$refs.scrollContainer, 'scrollTop', 0)
      })
    }
  }
}
</script>

<style lang="scss">
@import '~@/halifax-ui/src/scss/framework';
@import '~@/scss/variables';

.modal-vc {
  $height-header: $height-modal-vc-header;
  $module: '.modal-vc';

  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: $z-index-modal-vc;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  height: 100vh;

  &__scroll-container {
    height: 100%;
    overflow: hidden;

    @include supports-scroll-snap-points {
      -webkit-scroll-snap-type: mandatory;
      scroll-snap-type: mandatory;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }
  }
  &__content {
    margin-top: 100vh;
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    scroll-snap-align: start;
    max-width: $max-width-container;
    margin-left: auto;
    margin-right: auto;

    // Dock to bottom in fullscreen environments because we don't have to deal with the
    // appearing / disappearing browser chrome
    html:not(.platform--browser) & {
      margin-bottom: 0 !important;
    }
  }
  &__header {
    display: flex;
    width: 100%;
    justify-content: space-between;
    color: #fff;
    font-weight: 700;
    line-height: $height-header;

    @include supports-scroll-snap-points {
      background-image: url('data:image/svg+xml, <svg viewBox="0 0 68 5" xmlns="http://www.w3.org/2000/svg"><rect fill="hsla(0, 0%, 100%, .2)" width="68" height="5" rx="2.5" ry="2.5" /></svg>');
      background-size: 68px 5px;
      background-position: center;
      background-repeat: no-repeat;
    }
    @each $name, $color in $burger_colors {
      $c: '' + #{$name};
      &--#{$c} {
        background-color: #{$color};
      }
    }
  }
  &__heading {
    min-width: 100px;
    padding-left: 15px;
    font-size: 1.6rem;
  }
  &__close {
    position: relative;
    min-width: 36px;
    padding: 0;
    margin: 4px;
    background: none;
    border: none;
    border-radius: 3px;

    &:active {
      background-color: rgba(#000, 0.03);
      color: #ddd;
    }
    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 17px;
      right: 10px;
      width: 16px;
      height: 3px;
      background-color: currentColor;
      transform: rotate(45deg);
    }
    &::after {
      transform: rotate(-45deg);
    }
  }
  &__backdrop {
    background: hsla(193, 9%, 14%, 0.85);
    position: fixed;
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    will-change: opacity;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
  }
}
</style>
